<template>
  <div :class="rangeInputClass">
    <div
      v-if="showHistogram"
      class="histogram-wrap"
    >
      <ExcursionChartByHistogram
        :histogram
        :max
        :min
      />
    </div>
    <div
      ref="wrap"
      class="slider-wrap"
      @click="onClick"
      @pointermove="onPointerMove"
      @pointerup="releasePointer"
      @pointercancel="releasePointer"
    >
      <div class="track" />
      <div
        class="gap"
        :style="gapStyle"
      />
      <div
        v-for="index in THUMB_INDEXES"
        :key="index"
        :class="thumbClasses[index]"
        :style="thumbStyle[index]"
        @click.stop
        @pointerdown.stop.prevent="onThumbPointerDown(index, $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { CSSProperties } from 'vue';
  import { computed, ref } from 'vue';
  import ExcursionChartByHistogram from './ExcursionChartByHistogram.vue';

  interface Props {
    min: number;
    max: number;
    histogram: Record<string, number>;
    value: Array<number>;
    isModal: boolean;
  }

  interface Emits {
    (e: 'input', value: Array<number>): void;
  }

  const emit = defineEmits<Emits>();

  const props = defineProps<Props>();

  const THUMB_INDEXES: (0 | 1)[] = [0, 1];

  const wasDragging = ref(false);
  const dragging = ref<null | number>(null);
  const wrap = ref<HTMLElement | null>(null);
  const pointerId = ref<number | null>(null);
  const dragOffsetPx = ref(0);

  const clamp = (value: number, min: number, max: number) => {
    return Math.min(Math.max(value, min), max);
  };

  const isRangeDisabled = computed(() => props.min >= props.max);
  const showHistogram = computed(() => {
    return Object.values(props.histogram).some((value) => value > 0);
  });
  const rangeInputClass = computed(() => ['range-slider', { 'show-histogram': showHistogram.value }]);
  const clampedValue = computed(() => {
    const [firstValue, secondValue] = props.value;

    return [clamp(firstValue, props.min, props.max), clamp(secondValue, props.min, props.max)];
  });

  const thumbClasses = computed(() => {
    return THUMB_INDEXES.map((index) => ({
      thumb: true,
      dragging: dragging.value === index,
    }));
  });

  const resetDragging = () => {
    dragging.value = null;
    pointerId.value = null;
    dragOffsetPx.value = 0;
  };

  const numberToPercent = (number: number) => {
    if (isRangeDisabled.value) {
      return 0;
    }

    const clampedNumber = clamp(number, props.min, props.max);

    if (props.min <= 0) {
      return ((clampedNumber - props.min) / (props.max - props.min)) * 100;
    }

    const minLog = Math.log(props.min);
    const maxLog = Math.log(props.max);
    const valueLog = Math.log(clampedNumber);

    return ((valueLog - minLog) / (maxLog - minLog)) * 100;
  };

  const percentToNumber = (percent: number) => {
    const clampedPercent = clamp(percent, 0, 1);

    if (props.min <= 0) {
      return Math.round(props.min + clampedPercent * (props.max - props.min));
    }

    const minLog = Math.log(props.min);
    const maxLog = Math.log(props.max);
    const value = Math.exp(minLog + clampedPercent * (maxLog - minLog));

    return Math.round(clamp(value, props.min, props.max));
  };

  const getTrackRect = () => wrap.value?.getBoundingClientRect() ?? null;

  const thumbStyle = computed<CSSProperties[]>(() => {
    const getStyle = (percent: number): CSSProperties => ({
      left: `${percent}%`,
    });

    if (isRangeDisabled.value) {
      return [getStyle(0), getStyle(100)];
    }

    return [getStyle(numberToPercent(clampedValue.value[0])), getStyle(numberToPercent(clampedValue.value[1]))];
  });

  const gapStyle = computed<CSSProperties>(() => {
    if (isRangeDisabled.value) {
      return {
        left: 0,
        right: 0,
      };
    }

    const firstPercent = numberToPercent(clampedValue.value[0]);
    const secondPercent = numberToPercent(clampedValue.value[1]);

    const leftPercent = Math.min(firstPercent, secondPercent);
    const rightPercent = Math.max(firstPercent, secondPercent);

    return {
      left: `${leftPercent}%`,
      right: `${100 - rightPercent}%`,
    };
  });

  const pxToNumber = (px: number) => {
    const rect = getTrackRect();

    if (!rect) {
      return props.min;
    }

    const percent = (px - rect.left) / rect.width;

    return percentToNumber(percent);
  };

  const closestHandle = (px: number) => {
    const rect = getTrackRect();

    if (!rect) {
      return 0;
    }

    const percent = clamp(((px - rect.left) / rect.width) * 100, 0, 100);

    const firstPercent = numberToPercent(clampedValue.value[0]);
    const secondPercent = numberToPercent(clampedValue.value[1]);

    const firstDistance = Math.abs(firstPercent - percent);
    const secondDistance = Math.abs(secondPercent - percent);

    if (percent < firstPercent) {
      return 0;
    }

    return firstDistance < secondDistance ? 0 : 1;
  };

  const onHandlePosChange = (index: number, px: number) => {
    if (isRangeDisabled.value) {
      return;
    }

    const value = clampedValue.value.slice();

    value[index] = pxToNumber(px);

    if (value[0] > value[1] && dragging.value !== null) {
      value.reverse();
      dragging.value = index ? 0 : 1;
    }

    value[0] = clamp(value[0], props.min, props.max);
    value[1] = clamp(value[1], props.min, props.max);

    // Эмитим изменения только если значение реально изменились
    if (value[0] !== props.value[0] || value[1] !== props.value[1]) {
      emit('input', value);
    }
  };

  const onClick = (event: MouseEvent) => {
    if (wasDragging.value) {
      wasDragging.value = false;

      return;
    }

    const index = closestHandle(event.clientX);

    onHandlePosChange(index, event.clientX);
  };

  const onThumbPointerDown = (index: number, event: PointerEvent) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const thumb = event.currentTarget as HTMLElement;
    const thumbRect = thumb.getBoundingClientRect();

    dragOffsetPx.value = event.clientX - (thumbRect.left + thumbRect.width / 2);
    dragging.value = index;
    pointerId.value = event.pointerId;

    wrap.value?.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent) => {
    if (dragging.value === null || pointerId.value !== event.pointerId) {
      return;
    }

    wasDragging.value = true;

    onHandlePosChange(dragging.value, event.clientX - dragOffsetPx.value);
  };

  const releasePointer = (event: PointerEvent) => {
    if (pointerId.value !== event.pointerId) {
      return;
    }

    if (wrap.value?.hasPointerCapture(event.pointerId)) {
      wrap.value.releasePointerCapture(event.pointerId);
    }

    resetDragging();
  };
</script>

<style scoped lang="scss">
  .range-slider {
    --handle-size: 24px;
    position: relative;
    padding: 0 24px 0 0;

    &:not(.show-histogram) {
      padding-top: 10px;
    }
  }

  .histogram-wrap {
    margin: 0 1px -28px;
    margin-right: -24px;
  }

  .slider-wrap {
    position: relative;
    height: var(--handle-size);
    margin: 16px 0 0;
    touch-action: none;
  }

  .track,
  .gap {
    position: absolute;
    top: 50%;
    height: 4px;
    transform: translateY(-50%);
  }

  .track {
    position: relative;
    left: 0;
    margin-right: -24px;
    border-radius: 2px;
    background: var(--alias-color-background-subtler);

    &:before {
      content: '';
      position: absolute;
      right: 0;
      bottom: 100%;
      left: 0;
      height: 16px;
      background: linear-gradient(180deg, transparent 0%, var(--alias-color-background-default) 100%);
      pointer-events: none;
    }
  }

  .gap {
    margin-left: 2px;
    background-color: var(--brand-color);
  }

  .thumb {
    position: absolute;
    top: 0;
    box-sizing: border-box;
    width: 24px;
    height: 24px;
    border: 4px solid var(--brand-color);
    border-radius: 50%;
    background: var(--alias-color-background-default);
    cursor: grab;
    touch-action: none;

    &:active {
      cursor: grabbing;
    }

    &.dragging {
      z-index: 1;
    }
  }
</style>
